import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Typography, Box, Chip, Tooltip
} from '@mui/material';
import api from '../functions/api'; // DAS-3127: sends the login token (raw axios got 401)
import InfoTooltip from './InfoTooltip';

const labelToChipColor = (label) => {
  if (!label || typeof label !== 'string') return 'default';
  const s = label.toLowerCase();
  if (s.includes('strong buying') || s.includes('strong buy')) return 'success';
  if (s.includes('weak buying') || s.includes('weak buy')) return 'success';
  if (s.includes('weak selling') || s.includes('weak sell')) return 'warning';
  if (s.includes('strong selling') || s.includes('strong sell')) return 'error';
  if (s.includes('neutral')) return 'default';
  return 'default';
};

const InstitutionalRegionOverview = () => {
  const institutionalTrends = useSelector(state => state.portfolio?.data?.Vector_institutional_trend || {});
  const sectorTrend = useSelector(state => state.portfolio?.data?.Sector_trend || {});
  const marketPositioningData = useSelector(state => state.portfolio?.data?.Market_Positioning_Analysis || {});

  const [sectorSummary, setSectorSummary] = useState([]);
  const [midRegionSectorRows, setMidRegionSectorRows] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [s1, s2] = await Promise.allSettled([
          api.get('/trendsearch_sector_summary'),
          api.get('/institutional_region_sector')
        ]);
        if (!cancelled && s1.status === 'fulfilled') {
          const d = s1.value?.data; if (d?.success) setSectorSummary(d.data || []);
        }
        if (!cancelled && s2.status === 'fulfilled') {
          const d = s2.value?.data; if (d?.success) setMidRegionSectorRows(d.data || []);
        }
      } catch (_) {}
    })();
    return () => { cancelled = true; };
  }, []);

  const regionList = useMemo(() => {
    const cols = (institutionalTrends?.columns || []).slice(2);
    const regions = Array.from(new Set(cols.map(col => {
      const name = String(col).replace(/^Institutional_trend_/, '');
      const parts = name.split('_');
      return parts[0];
    })));
    const desired = ['Global', 'USA', 'Europe', 'Japan', 'Taiwan', 'China'];
    const inDesired = desired.filter(r => regions.includes(r));
    const others = regions.filter(r => !desired.includes(r)).sort();
    return [...inDesired, ...others];
  }, [institutionalTrends]);

  const regionShort = {
    Global: 'Global',
    USA: 'US',
    Europe: 'EU',
    Japan: 'JP',
    Taiwan: 'TW',
    China: 'CN'
  };

  const rowsData = useMemo(() => {
    const normalize = (s) => String(s || '').toLowerCase().replace(/_/g, ' ').trim();
    const { columns = [], rows = [] } = institutionalTrends;
    const instRow = (rows || [])[0] || [];
    const instCols = (columns || []).slice(2);

    // Use normalized SQL rows for mid-term region/sector mapping (canonicalize names)
    // Keep long region names to match Vector_institutional_trend columns; convert SQL codes to long names
    const toLongRegion = (r) => {
      const m = {
        'US': 'USA', 'USA': 'USA', 'United States': 'USA',
        'EU': 'Europe', 'Europe': 'Europe',
        'CN': 'China', 'China': 'China',
        'JP': 'Japan', 'Japan': 'Japan',
        'TW': 'Taiwan', 'Taiwan': 'Taiwan',
        'Global': 'Global'
      };
      return m[String(r)] || String(r);
    };
    const sectorAlias = {
      'Consumer Discretionary': 'Consumer Cyclical',
      'Consumer Staples': 'Consumer Defensive'
    };
    const canonicalRegion = (r) => r;
    const canonicalSector = (s) => sectorAlias[s] || s;

    // Prefer latest-epoch SQL rows; canonicalize and index for exact lookups
    let regionSectorAll = [];
    if (Array.isArray(midRegionSectorRows) && midRegionSectorRows.length > 0) {
      const latestEpoch = midRegionSectorRows.reduce((m, r) => Math.max(m, Number(r.epoch || 0)), 0);
      const latestRows = midRegionSectorRows.filter(r => Number(r.epoch || 0) === latestEpoch);
      regionSectorAll = latestRows.map(r => ({
        region: toLongRegion(r.region),
        sector: canonicalSector(r.sector),
        pick: r.pick
      }));
    }

    // Fallback to JSON/HTML if table not yet populated
    if (regionSectorAll.length === 0 && marketPositioningData?.rows && marketPositioningData?.rows[0]) {
      try {
        const mj = marketPositioningData.rows[0][marketPositioningData.columns.indexOf('full_result_json')];
        const json = typeof mj === 'string' ? JSON.parse(mj) : mj;
        if (json?.region_sector_map) {
          for (const [region, sectorMap] of Object.entries(json.region_sector_map)) {
            for (const [sector, pick] of Object.entries(sectorMap)) {
              regionSectorAll.push({ region: canonicalRegion(region), sector: canonicalSector(sector), pick });
            }
          }
        } else {
          const resultHtml = marketPositioningData.rows[0][marketPositioningData.columns.indexOf('result')];
          if (typeof resultHtml === 'string' && resultHtml.includes('<table')) {
            const matches = [...resultHtml.matchAll(/<tr><td>([^<]+)<\/td><td>([^<]+)<\/td><td>(?:<span[^>]*>)?([^<]+)(?:<\/span>)?<\/td><\/tr>/g)];
            regionSectorAll = matches.map(m => ({ region: canonicalRegion(m[1]), sector: canonicalSector(m[2]), pick: m[3] }));
          }
        }
      } catch (e) {}
    }

    const sectors = (sectorTrend?.rows || []).map(r => r[2]).filter(Boolean);

    return sectors.map(sectorName => {
      // Short-by-region from Vector_institutional_trend
      const shortBy = {};
      regionList.forEach(region => {
        let signal = null;
        for (let idx = 0; idx < instCols.length; idx++) {
          const col = instCols[idx];
          const name = String(col).replace(/^Institutional_trend_/, '');
          const parts = name.split('_');
          const colRegion = parts[0];
          const sectorPart = parts.slice(1).join(' ');
          if (colRegion === region && normalize(sectorPart) === normalize(sectorName)) {
            signal = instRow[idx + 2];
            break;
          }
        }
        if (typeof signal === 'string' && signal.length > 0) shortBy[region] = signal;
      });

      // Mid-by-region from normalized SQL rows (indexed lookup)
      const midBy = {};
      const index = new Map();
      for (const item of regionSectorAll) {
        index.set(`${normalize(item.region)}|${normalize(item.sector)}`, item.pick);
      }
      regionList.forEach(region => {
        const key = `${normalize(region)}|${normalize(sectorName)}`;
        const pick = index.get(key);
        if (pick) midBy[region] = pick;
      });

      // Aggregated (Short ∩ Mid) from Vector_institutional_trend aggregated fields
      const aggregatedBy = {};
      // Find aggregated columns once
      const aggCols = (columns || []).filter(c => String(c).startsWith('Institutional_aggregated_trend_'));
      regionList.forEach(region => {
        const field = `Institutional_aggregated_trend_${region.replace(/ /g, '_')}_${String(sectorName).replace(/ /g, '_')}`;
        const idx = (columns || []).indexOf(field);
        if (idx >= 0) {
          const val = instRow[idx];
          if (typeof val === 'string' && val.length > 0) aggregatedBy[region] = val;
        } else {
          // Some DBs may have different ordering; search in aggCols for matching suffix
          const match = aggCols.find(c => normalize(String(c).replace('Institutional_aggregated_trend_', '')) === normalize(`${region} ${sectorName}`));
          if (match) {
            const j = (columns || []).indexOf(match);
            const v = instRow[j];
            if (typeof v === 'string' && v.length > 0) aggregatedBy[region] = v;
          }
        }
      });

      return { sector: sectorName, shortBy, midBy, aggregatedBy };
    });
  }, [institutionalTrends, midRegionSectorRows, sectorTrend, regionList, marketPositioningData]);

  return (
    <TableContainer component={Paper} sx={{ maxHeight: 'none' }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 'bold' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                Sector
                <InfoTooltip text="Financial sector classification" placement="top" />
              </Box>
            </TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                Institutional (Short) by Region
                <InfoTooltip text="Short-term institutional flow signal per region (ETF A/D Line, OBV)." placement="top" />
              </Box>
            </TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                Institutional (Mid) by Region
                <InfoTooltip text="Mid-term institutional positioning per region from aggregated ETF flows." placement="top" />
              </Box>
            </TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                Aggregated (Short ∩ Mid)
                <InfoTooltip text="Only when short- and mid-term converge: same direction; chooses stronger magnitude. Otherwise empty." placement="top" />
              </Box>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rowsData.map((row, idx) => (
            <TableRow key={idx} sx={{ '&:nth-of-type(odd)': { backgroundColor: '#fafafa' } }}>
              <TableCell>
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{row.sector}</Typography>
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {Object.keys(row.shortBy).map((region) => {
                    const val = row.shortBy[region];
                    return (
                      <Tooltip key={region} title={`${region}: ${val}`}>
                        <Chip size="small" label={`${regionShort[region] || region}`} color={labelToChipColor(val)} />
                      </Tooltip>
                    );
                  })}
                </Box>
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {Object.keys(row.midBy).map((region) => {
                    const val = row.midBy[region];
                    return (
                      <Tooltip key={region} title={`${region}: ${val}`}>
                        <Chip size="small" label={`${regionShort[region] || region}`} color={labelToChipColor(val)} />
                      </Tooltip>
                    );
                  })}
                </Box>
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {Object.keys(row.aggregatedBy || {}).map((region) => {
                    const val = row.aggregatedBy[region];
                    return (
                      <Tooltip key={region} title={`${region}: ${val}`}>
                        <Chip size="small" label={`${regionShort[region] || region}`} color={labelToChipColor(val)} />
                      </Tooltip>
                    );
                  })}
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default InstitutionalRegionOverview;


